import { Engine, Entity } from "../../engine/Engine";
import { SugarModel } from "../../loader/SugarModel";
import { SugarModelConfigurator } from "../../loader/SugarModelConfigurator";
import type { SugarModelJoint } from "../../loader/SugarModelJoint";
import type { Room } from "../../shape/Scene";
import {
  DESIGNER_JOINT_SNAP_COMMIT,
  DESIGNER_CONFIGURATOR_MODEL_DETACHED,
  type DesignerJointSnapCommitDetail,
  type DesignerConfiguratorModelDetachedDetail,
} from "../drag/constraints/JointSnapConstraint";
import {
  CallbackConfiguratorCompositionHost,
  centerConfiguratorFromDirectChildren,
  ConfiguratorComposition,
  type ConfiguratorCompositionHost,
} from "./ConfiguratorComposition";
import {
  registerConfiguratorComposition,
  unregisterConfiguratorComposition,
} from "./ConfiguratorCompositionRegistry";
import type { DesignerElementEventChannels } from "../events/ElementEventPlatform";
import { DesignerChannels } from "../events/DesignerChannels";
import {
  DESIGNER_REQUEST_RENDER_COMMAND,
  DESIGNER_SCENE_CHANGED_EVENT,
  DESIGNER_SELECT_PRODUCT_COMMAND,
} from "../events/DesignerEventContracts";

type SnapPair = {
  baseJoint: SugarModelJoint;
  tailJoint: SugarModelJoint;
  baseModel: SugarModel;
  tailModel: SugarModel;
};

/** Composition + joint-snap commit host. Shared by RD and CFG `<configurator-bridge>`. */
export class DesignerConfiguratorBridge extends Entity {
  private readonly _composition: ConfiguratorComposition;

  /** Always present — falls back to a private platform (see DesignerChannels). */
  private readonly _channels: DesignerChannels;

  constructor(
    private _scene: Room,
    _events?: DesignerElementEventChannels,
    host?: ConfiguratorCompositionHost,
  ) {
    super();
    this._channels = new DesignerChannels(_events);
    this._composition = new ConfiguratorComposition(
      _scene.uuid,
      host ??
        new CallbackConfiguratorCompositionHost({
          objects: () =>
            _scene.models.filter((model): model is SugarModel =>
              model instanceof SugarModel,
            ),
          addRoot: (model) => {
            this.engine?.activeScene?.add(model);
            if (!_scene.models.includes(model)) _scene.addModel(model);
          },
          removeRoot: (model) => {
            _scene.removeModel(model);
            model.removeFromParent();
          },
          createConfigurator: () => this._newConfigurator(),
        }),
    );
    registerConfiguratorComposition(_scene, this._composition);
  }

  override onAdd(_engine: Engine): void {
    this._channels.bindEngine(_engine);
    this._scene.addEventListener(
      DESIGNER_JOINT_SNAP_COMMIT,
      this._onJointSnapCommit as EventListener,
    );
    this._scene.addEventListener(
      DESIGNER_CONFIGURATOR_MODEL_DETACHED,
      this._onConfiguratorModelDetached as EventListener,
    );
  }

  override onRemove(_engine: Engine): void {
    this._scene.removeEventListener(
      DESIGNER_JOINT_SNAP_COMMIT,
      this._onJointSnapCommit as EventListener,
    );
    this._scene.removeEventListener(
      DESIGNER_CONFIGURATOR_MODEL_DETACHED,
      this._onConfiguratorModelDetached as EventListener,
    );
    unregisterConfiguratorComposition(this._scene);
    this._composition.dispose();
  }

  private _onConfiguratorModelDetached = (event: Event): void => {
    const detail = (
      event as CustomEvent<DesignerConfiguratorModelDetachedDetail>
    ).detail;

    if (detail.configurator) {
      this._composition.adopt(detail.configurator);
      centerConfiguratorFromDirectChildren(detail.configurator);
      detail.configurator.updateMatrixWorld(true);
      detail.configurator.updateWorldMatrix(true, true);
    }
  };

  private _onJointSnapCommit = (event: Event): void => {
    const detail = (event as CustomEvent<DesignerJointSnapCommitDetail>).detail;
    if (!detail?.baseJoint || !detail?.tailJoint) return;

    const pair: SnapPair = {
      baseJoint: detail.baseJoint,
      tailJoint: detail.tailJoint,
      baseModel: detail.baseJoint.model,
      tailModel: detail.tailJoint.model,
    };

    if (!pair.baseModel || !pair.tailModel) return;
    if (pair.baseModel === pair.tailModel) return;

    const baseConfigurator = this._getConfigurator(pair.baseModel);
    const tailConfigurator = this._getConfigurator(pair.tailModel);

    if (baseConfigurator && tailConfigurator) {
      if (baseConfigurator !== tailConfigurator) {
        this._createConfiguratorFromConfigurators(
          baseConfigurator,
          tailConfigurator,
        );
      }
      return;
    }

    if (baseConfigurator || tailConfigurator) {
      this._addRawModelToExistingConfigurator(
        baseConfigurator ?? tailConfigurator!,
        baseConfigurator ? pair.tailModel : pair.baseModel,
      );
      return;
    }

    this._createConfiguratorFromPair(pair);
  };

  private _getConfigurator(
    model: SugarModel,
  ): SugarModelConfigurator | undefined {
    let configurator =
      model instanceof SugarModelConfigurator
        ? model
        : model.configurator instanceof SugarModelConfigurator
          ? model.configurator
          : undefined;

    while (configurator?.configurator instanceof SugarModelConfigurator) {
      configurator = configurator.configurator;
    }

    return configurator;
  }

  private _wireConfigurator(configurator: SugarModelConfigurator): void {
    configurator.addEventListener("configured" as any, () => {
      this._channels.channels.configuration.events.emit(
        DESIGNER_SCENE_CHANGED_EVENT,
        this._scene,
      );
      this._requestRender();
    });
  }

  private _selectConfigurator(configurator: SugarModelConfigurator): void {
    this._channels.channels.selection.commands.emit(
      DESIGNER_SELECT_PRODUCT_COMMAND,
      { model: configurator },
    );
  }

  private _requestRender(): void {
    this._channels.channels.transform.commands.emit(
      DESIGNER_REQUEST_RENDER_COMMAND,
      undefined,
    );
  }

  private _addRawModelToExistingConfigurator(
    configurator: SugarModelConfigurator,
    model: SugarModel,
  ): void {
    if (model instanceof SugarModelConfigurator) return;
    if (model.configurator === configurator) return;

    this._composition.attach(model, configurator);
    this._selectConfigurator(configurator);
    this._requestRender();
  }

  private _createConfiguratorFromConfigurators(
    firstConfigurator: SugarModelConfigurator,
    secondConfigurator: SugarModelConfigurator,
  ): void {
    const parent = this._composition.merge(
      firstConfigurator,
      secondConfigurator,
    );

    this._selectConfigurator(parent);
    this._requestRender();
  }

  private _createConfiguratorFromPair(pair: SnapPair): void {
    const configurator = this._composition.create([
      pair.baseModel,
      pair.tailModel,
    ]);

    this._selectConfigurator(configurator);
    this._requestRender();
  }

  private _newConfigurator(): SugarModelConfigurator {
    const configurator = new SugarModelConfigurator(
      new Map(),
      new Map(),
      new Map(),
    );

    this._wireConfigurator(configurator);
    return configurator;
  }
}
